import {Component, Inject, EventEmitter, ViewChild} from '@angular/core';
import {BlobId, ExpeditionId, ProjectId, SpecId} from '../../types/ids';
import {ActivatedRoute, Router} from '@angular/router';
import {getRandomUUID, isDefined, produceCanonicalShortURL} from '../../utils/utils';
import {WmNotificationService} from '../../components/notification/wm-notification.service';

import {BrowserSpecification, DriverSpecificationType, ObservationTarget, SpecCheckTestConfig, TestObjectsSourceSpec, TestRun} from '@webmate-web-core/shared/types/testmgmt';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {BlobUploadToolComponent, BlobUploadToolConfig} from "@webmate-web-core/shared/tools/blob-upload/blob-upload-tool.component";
import {CreateTestExecutionResult, TestService} from "@webmate-web-core/shared/services/test.service";
import {Browser, browserFromBackendObject} from "@webmate-web-core/shared/types/browser";
import {ModalToolContainerService, WebmateToolConfig} from '../modaltoolcontainer/modaltoolcontainer.service';
import {TOOL_CONTAINER_DATA} from '../modaltoolcontainer/modaltoolcontainer.component';
import {VehicleService} from "@webmate-web-core/shared/services/vehicle.service";
import {first, map, mergeMap, retry, tap} from "rxjs/operators";
import {Observable, timer} from "rxjs";
import {BrowserSessionService} from "@webmate-web-core/shared/services/browsersession.service";
import {WorkbenchOpenService} from '@webmate-web-core/shared/workbench-ipc';
import {faPlus} from '@fortawesome/pro-solid-svg-icons';
import {CreateStateCollectionToolComponent, CreateStateCollectionToolConfig, StateIdAndName} from '@webmate-web-core/projects/project/auditlab/audits/create-statecollection-tool.component';
import {Popover} from 'primeng/popover';
import {
    CreateTestObjectsSourceComponent,
    CreateTestObjectsSourceToolConfig,
    TestObjectsSourceType
} from '@webmate-web-core/projects/project/auditlab/audits/create-testobjects-source.component';
import {AutomationPolicyConfig} from '@webmate-web-core/shared/types/browsersession';

export class SpecCheckTestDialogToolConfig implements WebmateToolConfig {
    toolName = "Create New Spec Check";
    scrollable = true;

    constructor(public projectId: ProjectId, public defaults: SpecCheckTestConfig, public createCallback: (conf: SpecCheckTestConfig, isCheck: Boolean) => void, public closeAfterCallback: boolean,
                public editMode: boolean = false, public existingNames: Array<string> = [], public btnLabel = 'EXECUTE TEST') {
        if (defaults.name) {
            this.toolName =  `Configure ${defaults.name}`;
        }
    }
}

@Component({
    templateUrl: './spec-check-test-dialog.component.html',
    styleUrls: ['./spec-check-test-dialog.component.less'],
})
export class SpecCheckTestDialogComponent {

    @ViewChild('addSpecPopover') addSpecPopover: Popover;

    projectId: ProjectId;

    creatingTest = false;

    launchLabel: string = 'Launching Test ...';
    errorMsg: string = '';

    availableBrowsers: Browser[] | undefined;

    emailContentCheckingEnabled: boolean = false;

    stateContentCheckingEnabled: boolean = false;

    stateLayoutCheckingEnabled: boolean = false;

    privacyCheckingEnabled: boolean = false;

    stateA11yCheckingEnabled: boolean = false;

    browser: Browser | undefined;

    form: UntypedFormGroup;
    advancedSettingsShown = false;

    // TODO: should come from backend
    notificationTopics = [
        {topicName: 'General Slack and Email for Project', topicId: 'slack_and_email_for_project'},
        {topicName: 'Alerting Only', topicId: 'alerting_only'},
    ];
    private lastShownTestObjectsSourceTool?: TestObjectsSourceType;

    constructor(@Inject(TOOL_CONTAINER_DATA) public data: SpecCheckTestDialogToolConfig, private route: ActivatedRoute, private router: Router,
                private webmateNotificationService: WmNotificationService,
                private vehicleService: VehicleService,
                private browserSessionService: BrowserSessionService,
                private toolService: ModalToolContainerService, private fb: UntypedFormBuilder, private testService: TestService,
                private readonly workbenchOpenService: WorkbenchOpenService) {
        this.projectId = data.projectId;
    }
    busy: boolean = false;

    private _urlValid(url: string){
        if (url.indexOf('.') === -1) return false;
        if (url.indexOf('://') === -1) return false;
        if (!url.startsWith('http://') && !url.startsWith('https://')) return false;
        if (url.indexOf('://') !== url.lastIndexOf('://')) return false;
        return true;
    }

    existingURLValid(idx: number): boolean {
        if (idx >= this.form.value['urls'].length) return false;

        let url = this.form.value['urls'][idx];
        if (url === undefined || url.length === 0) return false;

        return this._urlValid(url);
    }

    interactiveStartUrlValid(): boolean {
        const url = this.form.value['interactiveStartUrl']?.trim();
        if (!url) return true;

        return this._urlValid(url);
    }

    allURLsValid(): boolean {
        for (let i = 0; i < this.form.value['urls'].length; ++i) {
            if (!this.existingURLValid(i)) {
                return false;
            }
        }
        return true;
    }

    addUrlListItem() {
        let items = this.form.get('urls') as UntypedFormArray;
        items.push(this.fb.control(''));
    }

    removeUrlListItem(index: number) {
        let items = this.form.get('urls') as UntypedFormArray;
        items.removeAt(index);
    }

    clearUrl(index: number) {
        (this.form.get('urls') as UntypedFormArray).at(index).setValue("");
    }

    urlTrackByIndex(index: number, obj: any): any {
        return index;
    }

    addSpecs(specs: SpecId[]) {
        console.log("Adding specs: ", specs);
        this.addSpecPopover.hide();
        this.form.patchValue({'selectedSpecs': [...specs]});
    }

    addTargets(targets: ObservationTarget[]) {
        this.form.patchValue({'selectedTargets': [...(this.form.value.selectedTargets || []), ...targets]});
    }

    createCheck(isCheck: boolean) {
        const conf: SpecCheckTestConfig = {
            name: this.form.value.name,
            testObjects: {
                type: 'WMObject',
                data: this.form.value.selectedTargets,
            },
            specs: {
                type: 'SpecRefList',
                data: this.form.value.selectedSpecs,
            },
            planningStrategy: {
                type: 'WMObject',
                data: {
                    "type": "AllSpecsForAllTestObjectsStrategy"
                }
            },
            automationPolicy: this.form.value.automationPolicy,
        }

        this.data.createCallback(conf, isCheck);
        if (this.data.closeAfterCallback) {
            this.toolService.closeLastTool();
        }
    }

    selectTargets(event: MouseEvent) {
        this.toolService.openTool(CreateTestObjectsSourceComponent, new CreateTestObjectsSourceToolConfig('Add Targets', this.projectId,
            (sourceSpec: TestObjectsSourceSpec) => {
                this.form.patchValue({'selectedTargets': sourceSpec});
                this.lastShownTestObjectsSourceTool = this.mapSourceSpecTypeToToolType(sourceSpec);
            },
            this.lastShownTestObjectsSourceTool
        ));

    }

    selectedTargetsSummary(): string {
        const sourceSpec = this.form?.value?.selectedTargets as TestObjectsSourceSpec | undefined;
        if (!sourceSpec) {
            return 'No source selected.';
        }

        if (sourceSpec.type === 'URLListSourceSpec') {
            return `${sourceSpec.urls?.length || 0} URL target(s) selected.`;
        } else if (sourceSpec.type === 'TestObjectListSourceSpec') {
            return `${sourceSpec.testObjectIds?.length || 0} Test Object(s) selected.`;
        } else if (sourceSpec.type === 'CrawledSourceSpec') {
            return `${sourceSpec.seed?.urls?.length || 0} crawl seed URL(s) selected.`;
        } else if (sourceSpec.type === 'TestObjectCollectionSourceSpec') {
            return '1 Test Object Collection selected.';
        }

        return '1 source selected.';
    }

    private mapSourceSpecTypeToToolType(sourceSpec: TestObjectsSourceSpec): TestObjectsSourceType | undefined {
        if (sourceSpec.type === 'URLListSourceSpec') {
            return 'urllist';
        } else if (sourceSpec.type === 'TestObjectListSourceSpec') {
            return 'testobjectlist';
        } else if (sourceSpec.type === 'CrawledSourceSpec') {
            return 'crawled';
        } else if (sourceSpec.type === 'TestObjectCollectionSourceSpec') {
            return 'testobjectcollection';
        }

        return undefined;
    }

    reset() {
        this.advancedSettingsShown = !!this.data.defaults?.automationPolicy;

        const formParams: any = {
            name: this.data.defaults?.name || 'Specification check',
            driverType: 'urllist',
            urls: [''],
            scheduleIntervalValue: 1,
            selectedSpecs: this.data.defaults?.specs?.data || [],
            selectedTargets: this.data.defaults?.testObjects?.data,
            scheduleIntervalUnit: 'weeks',
            allowMonday: true,
            allowTuesday: true,
            allowWednesday: true,
            allowThursday: true,
            allowFriday: true,
            allowSaturday: false,
            allowSunday: false,
            notificationTopicIds: [],
            automationPolicy: this.data.defaults?.automationPolicy,
        };
        this.form = this.fb.group({
            'name': [formParams.name],
            'driverType': [formParams.driverType],
            'urlListBlobId': [formParams.urlListBlobId],
            'stateA11yCheckingEnabled': [formParams.stateA11yCheckingEnabled],
            'stateLayoutCheckingEnabled': [formParams.stateLayoutCheckingEnabled],
            'stateContentCheckingEnabled': [formParams.stateContentCheckingEnabled],
            'emailContentCheckingEnabled': [formParams.emailContentCheckingEnabled],
            'scheduleIntervalValue': [formParams.scheduleIntervalValue],
            'scheduleIntervalUnit': [formParams.scheduleIntervalUnit],
            'selectedSpecs': [formParams.selectedSpecs || []],
            'selectedTargets': [formParams.selectedTargets],
            'urls': this.fb.array(formParams.urls || []),
            'interactiveStartUrl': '',
            'allowMonday': [formParams.allowMonday || false],
            'allowTuesday': [formParams.allowTuesday || false],
            'allowWednesday': [formParams.allowWednesday || false],
            'allowThursday': [formParams.allowThursday || false],
            'allowFriday': [formParams.allowFriday || false],
            'allowSaturday': [formParams.allowSaturday || false],
            'allowSunday': [formParams.allowSunday || false],
            'notificationTopicIds': [formParams.notificationTopicIds || []],
            'automationPolicy': [formParams.automationPolicy as AutomationPolicyConfig | undefined],
        });

        const selectedTargets = formParams.selectedTargets as TestObjectsSourceSpec | undefined;
        if (selectedTargets) {
            this.lastShownTestObjectsSourceTool = this.mapSourceSpecTypeToToolType(selectedTargets);
        }
    }

    toggleAdvancedSettings() {
        this.advancedSettingsShown = !this.advancedSettingsShown;
    }

    ngOnInit() {
        this.vehicleService.getVehicleOffersForProject$(this.projectId).subscribe(offers => {
            const browsers = offers.map(o => browserFromBackendObject(o.properties['vehicle.browser']));
            this.availableBrowsers = browsers
                .filter(b => b.isActualBrowser())
                .filter(b => !b.isMobile())
                .sort((a, b) => a.browserType.localeCompare(b.browserType))
            this.busy = false;
        });

        this.reset();
    }

    openUrlListUploadDialog(event: MouseEvent) {
        this.toolService.openTool(BlobUploadToolComponent, new BlobUploadToolConfig(this.projectId, blob => this.handleBlobUpdate(blob)));
    }

    handleBlobUpdate(blobId: BlobId) {
        this.form.patchValue({'urlListBlobId': blobId});
    }

    setBrowser(browser: Browser) {
        this.browser = browser;
    }

    createTest() {
        this.creatingTest = true;
        let driverSpec = {
            type: 'InteractiveDriverSpecification' as DriverSpecificationType
        };

        if (this.form.value.driverType === 'urlfile') {
            driverSpec['urlListBlobId'] = this.form.value.urlListBlobId;
        } else if (this.form.value.driverType === 'urllist') {
            driverSpec['urlList'] = this.form.value.urls;
        } else if (this.form.value.driverType === 'interactive') {
            const startUrl = this.form.value.interactiveStartUrl?.trim();
            if (startUrl) {
                driverSpec['startUrl'] = startUrl;
            }
        } else {
            console.warn(`driver type ${this.form.value.driverType} not implemented yet`)
        }


        const requestedFacts = [
            ...(this.form.value.emailContentCheckingEnabled ? [ { factType: { finding: 'Mail.Content'} } ] : []),
        ];

        const expedition$ = this.testService.createAndStartTestExecution(this.projectId, 'f496aced-ce17-4252-854c-1e8ca1979544', this.form.value.name, {
            type: 'SingleTestRunCreationSpec',
            assignmentSpec: {
                'expeditionSpec': {
                    data: {
                        type: 'LiveExpeditionSpec',
                        driverSpec: driverSpec,
                        requestedFacts: requestedFacts,
                        vehicleSpec: {
                            browser: this.browser,
                            useProxy: false,
                            type: 'BrowserSpecification'
                        } as BrowserSpecification
                    },
                    type: 'ExpeditionSpec'
                }
            }
        }, undefined, this.form.value.automationPolicy).pipe(
            // waiting for expedition
            mergeMap(({testRunId}) => this.testService.getTestRun(testRunId).pipe(retry({count: 10, delay: 2000}), first((testRun: TestRun) => (testRun.expeditions.length > 0)))),
            map((testRun: TestRun) => testRun.expeditions[0] as ExpeditionId),
            tap(() => this.launchLabel = 'Waiting for browser ...')
        );
        this.browserSessionService.awaitBrowserSessionReady$(expedition$).subscribe({
            next: (session) => {
                this.creatingTest = false;
                this.toolService.disposeToolContainer();
                this.reset();
                this.workbenchOpenService.openConsoleForBrowserSession(this.projectId, session);
            }, error: (err) => {
                console.error("Failed to create expedition", err);
                this.errorMsg = 'Test creation failed.';
            }
        });
    }

    settingsValid() {
        return this.form.value.selectedSpecs.length > 0 && this.form.value.selectedTargets;
    }

    nameInvalid() {
        // return this.name.trim().length === 0 || this.data.existingNames.find(n => n === this.name.trim());
    }

    protected readonly faPlus = faPlus;
}
